import React, { useContext, useEffect, useRef } from "react";

import isEmpty from "lodash.isempty";
import { reaction } from "mobx";
import { observer } from "mobx-react";
import { useRouter } from "../../../hooks/useNavigation";
import pluralize from "pluralize";
import PropTypes from "prop-types";
import styled from "styled-components";

import { useAppState } from "../../../../contexts/ApplicationStateProvider";
import ComponentContext from "../../../../contexts/ComponentContext";
import { useStores } from "../../../../contexts/StoreContext";
import breakpoints from "../../../constants";
import Loader from "../../Loader/Loader";
import FilterBoxes from "./FilterBoxes";
import VacancyList from "./InifiniteVacancyList";
import Vacancy from "./Vacancy";

const StyledVacancyListWrapper = styled.div`
  min-height: 450px;
  max-height: 100vh;
  overflow: auto;
  width: 100%;

  @media (min-width: ${breakpoints.DESKTOP}) {
    max-height: 95vh;
    width: ${({ hasVacancies }) => (hasVacancies ? "50%" : "100%")};
  }

  scrollbar-color: ${({ $scrollBarColour }) =>
    `${$scrollBarColour || "initial"}`};
`;

const StyledVacancyListContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const StyledWrapper = styled.div`
  @media (min-width: ${breakpoints.DESKTOP}) {
    max-height: 90vh;
    min-height: 90vh;
  }
`;

const StyledVacancyWrapper = styled.div`
  width: 50%;
  background-color: transparent;
`;

const StyledHeader = styled.h2`
  color: ${({ textColour }) => `${textColour || "initial"}`};
`;
function CardsListWithVacancyView(props) {
  const { isLoading, thumbnailUrl } = props;
  const {
    additionalConfig,
    displayVancanciesCount = true,
    scrollBarColour,
    vacancyCountTextColour,
    vacancyCountClassName,
  } = useContext(ComponentContext);

  const router = useRouter();

  const { filtersStore, vacancyStore } = useStores();
  const { searchResults, setSearchResults, pagination } = filtersStore;
  const { fetchSingleVacancy } = vacancyStore;

  const { isMobile } = useAppState();

  const vacancyListRef = useRef(null);

  useEffect(() => {
    return () => {
      setSearchResults([]);
    };
  }, []);

  useEffect(() => {
    if (vacancyListRef?.current?.scrollTop) {
      vacancyListRef.current.scrollTop = 0;
    }
  }, [router]);

  useEffect(
    () =>
      reaction(
        () => searchResults[0]?.id,
        (id) => {
          if (id) {
            const matchingVacancy = searchResults.find(
              (vacancy) => vacancy.id === id
            );

            fetchSingleVacancy(matchingVacancy);
          }
        },
        { fireImmediately: true }
      ),
    []
  );

  return (
    <div className="row g-0">
      <FilterBoxes />
      {isEmpty(searchResults) ? null : (
        <StyledHeader
          textColour={vacancyCountTextColour}
          className={vacancyCountClassName}
        >
          {isLoading ? (
            <Loader />
          ) : (
            pluralize(
              "Vacancy",
              pagination.totalResults,
              displayVancanciesCount
            )
          )}
        </StyledHeader>
      )}
      <StyledVacancyListContainer>
        <StyledVacancyListWrapper
          hasVacancies={!isEmpty(searchResults)}
          $scrollBarColour={scrollBarColour}
          ref={vacancyListRef}
          className="py-4 py-lg-5"
          id="infinite-scroll-default-search-results"
        >
          {isLoading ? <Loader minHeight="560px" /> : <VacancyList />}
        </StyledVacancyListWrapper>
        {!isMobile && !isEmpty(searchResults) && (
          <StyledVacancyWrapper
            backgroundColor={additionalConfig?.vacancy?.backgroundColor}
          >
            {isLoading ? (
              <Loader minHeight="560px" />
            ) : (
              <StyledWrapper className="d-flex flex-column px-4">
                <Vacancy thumbnailUrl={thumbnailUrl} />
              </StyledWrapper>
            )}
          </StyledVacancyWrapper>
        )}
      </StyledVacancyListContainer>
    </div>
  );
}

export default observer(CardsListWithVacancyView);

CardsListWithVacancyView.propTypes = {
  isLoading: PropTypes.bool,
  thumbnailUrl: PropTypes.string,
};

CardsListWithVacancyView.defaultProps = {
  isLoading: false,
  thumbnailUrl: null,
};
